"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { browserTimezone, shortenTimezone } from "@gigpro/utils/datetime";
import React from "react";
import { Clock } from "../../Icons";
import {
  ARIA_VALUE_MAX,
  createKeyDownHandler,
  dateToSegments,
  formatSegment,
  getAriaValueNow,
  SEGMENT_ORDER,
  type SegmentState,
  type SegmentType,
  segmentsToDate,
  type TimeFieldProps,
} from "./helpers";

export type { TimeFieldProps } from "./helpers";

/**
 * Segmented 12-hour time input with keyboard navigation.
 *
 * Renders three focusable spinbutton segments (hour, minute, am/pm) that support:
 * - Digit typing with auto-advance to next segment
 * - Arrow keys to increment/decrement, PageUp/PageDown for larger steps
 * - Home/End to jump to min/max values
 * - Backspace to clear, second backspace focuses previous segment
 *
 * Each segment uses role="spinbutton" with full ARIA value attributes.
 * Built as a lightweight alternative to react-aria's useTimeField (~100KB)
 * while covering the same keyboard/a11y patterns for en-US 12h time.
 *
 * Limitations vs react-aria: no RTL, no composition/IME handling, no
 * iOS VoiceOver textbox fallback, no live region announcements.
 */
export const TimeField = ({
  ref: _ref,
  value,
  onValueChange,
  timezone = shortenTimezone(browserTimezone),
  hideTimezone = false,
  disabled = false,
  invalid = false,
  isSaving = false,
  referenceTime,
  endAdornment,
  className,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  id,
  onBlur,
  ...rest
}: Readonly<TimeFieldProps>) => {
  const controlledSegments = dateToSegments(value, referenceTime);

  // Clearing a segment (e.g. backspace on minute) produces a state that can't
  // be represented as a Date. displayOverride holds this partial state locally
  // until all segments are filled again and we can emit a valid Date.
  const [displayOverride, setDisplayOverride] = React.useState<SegmentState | null>(null);
  const segments = displayOverride ?? controlledSegments;

  // When the parent updates the controlled value, discard any local override
  const currentMs = value?.getTime() ?? null;
  const [lastValueMs, setLastValueMs] = React.useState(currentMs);
  if (currentMs !== lastValueMs) {
    setLastValueMs(currentMs);
    if (displayOverride) setDisplayOverride(null);
  }

  const containerRef = React.useRef<HTMLDivElement>(null);
  // Buffer for multi-digit typing: "1" waits for second digit, "12" auto-advances
  const enteredKeys = React.useRef("");
  const [isFocused, setIsFocused] = React.useState(false);

  const updateSegments = (newSeg: SegmentState) => {
    const d = segmentsToDate(newSeg, value);
    if (d) {
      setDisplayOverride(null);
      onValueChange?.(d);
    } else {
      setDisplayOverride(newSeg);
      onValueChange?.(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, type: SegmentType, index: number) => {
    if (disabled) return;
    createKeyDownHandler({
      segments,
      updateSegments,
      focusSegment: (i: number) =>
        containerRef.current?.querySelector<HTMLElement>(`[data-type="${SEGMENT_ORDER[i]}"]`)?.focus(),
      enteredKeys,
    })(e, type, index);
  };

  const handleFocus = () => {
    enteredKeys.current = "";
    setIsFocused(true);
  };

  const handleSegmentBlur = (e: React.FocusEvent) => {
    enteredKeys.current = "";
    setIsFocused(false);
    if (displayOverride && !containerRef.current?.contains(e.relatedTarget)) {
      setDisplayOverride(null);
    }
  };

  return (
    <div
      {...rest}
      ref={containerRef}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget)) onBlur?.(e);
      }}
      className={cn(
        "inline-flex w-full items-center rounded-sm border-2 bg-white text-black",
        disabled && "bg-background",
        (!invalid || disabled) && "border-medium-gray",
        isFocused && "border-brand",
        invalid && !disabled && "border-error",
        "pr-2",
        className,
      )}
    >
      <fieldset
        id={id}
        aria-label={ariaLabel ?? "Time"}
        aria-describedby={ariaDescribedBy}
        className="flex w-full items-center px-2 py-0.5 text-large/5 font-regular outline-none"
      >
        {SEGMENT_ORDER.map((type, i) => (
          <React.Fragment key={type}>
            {i === 1 && <span aria-hidden="true">:</span>}
            {i === 2 && <span className="px-0.25" aria-hidden="true" />}
            <div
              role="spinbutton"
              aria-label={type === "dayPeriod" ? "am/pm" : type}
              aria-valuenow={getAriaValueNow(type, segments)}
              aria-valuemin={type === "hour" ? 1 : 0}
              aria-valuemax={ARIA_VALUE_MAX[type]}
              aria-valuetext={formatSegment(type, segments)}
              aria-invalid={invalid || undefined}
              aria-disabled={disabled || undefined}
              contentEditable={!disabled}
              suppressContentEditableWarning
              inputMode={type === "dayPeriod" ? "text" : "numeric"}
              tabIndex={disabled ? undefined : 0}
              data-type={type}
              onKeyDown={(e) => handleKeyDown(e, type, i)}
              onBeforeInput={(e) => {
                e.preventDefault();
                const data = e.nativeEvent.data;
                if (data) {
                  for (const char of data) {
                    handleKeyDown({ key: char, preventDefault: () => {} } as React.KeyboardEvent, type, i);
                  }
                }
              }}
              onFocus={handleFocus}
              onBlur={handleSegmentBlur}
              className={cn(
                "rounded-sm px-0.5 text-center tabular-nums outline-none select-none focus:bg-brand focus:text-white focus:caret-white",
                type !== "dayPeriod" && segments[type] == null && "text-medium-gray",
              )}
            >
              {formatSegment(type, segments)}
            </div>
          </React.Fragment>
        ))}
      </fieldset>
      {hideTimezone ? null : <p className="mr-1 text-small/5 whitespace-nowrap text-gray">({timezone})</p>}
      {isSaving && !disabled ? (
        <output aria-label="Saving" className="loading-spinner size-3.5 shrink-0 text-brand" />
      ) : (
        (endAdornment ?? <Clock className={cn("size-3.5 shrink-0 text-brand", disabled && "text-gray")} />)
      )}
    </div>
  );
};

TimeField.displayName = "TimeField";
